import { Grid, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  addDirectBilling,
  getClinicUsers,
} from "../../../redux/reducers/clinicSlice";
import {
  getClinicPetList,
  getPetByUserId,
} from "../../../redux/reducers/petSlice";
import { billAsForBillingHistory } from "../../../util/dropList";
import { getSessionData } from "../../../util/function";
import { readMandateSession } from "../../../util/mandateSessionStorage";

const initSaleValues = {
  billAs: "Pharmacy",
  searchType: "user",
  searchEnabled: false,
  user: null,
  pet: null,
  userPet: null,
  petSearch: "",
  searchValue: "",
  backDated: false,
  billDate: null,
  error: false,
  userPetErr: false,
};

/**
 * Shared Create Bill / Create Estimate customer-pet picker modal.
 * mode: "bill" | "estimate"
 */
const CreateBillEstimateModal = ({
  open,
  onClose,
  mode = "bill",
  isBillCombined = false,
  onCreated,
  onAddCustomer,
}) => {
  const dispatch = useDispatch();
  const searchReqIdRef = useRef(0);
  const [values, setValues] = useState(initSaleValues);
  const [saleSearchUsers, setSaleSearchUsers] = useState([]);
  const [saleSearchPets, setSaleSearchPets] = useState([]);
  const [petsByUserId, setPetsByUserId] = useState([]);

  const isEstimate = mode === "estimate";
  const petDataSharing = useMemo(() => {
    try {
      return (
        readMandateSession()?.dataSharing?.petData ??
        getSessionData()?.dataSharing?.petData ??
        false
      );
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setValues(initSaleValues);
      setSaleSearchUsers([]);
      setSaleSearchPets([]);
      setPetsByUserId([]);
    }
  }, [open]);

  useEffect(() => {
    setSaleSearchUsers([]);
    setSaleSearchPets([]);

    const value = values?.searchValue?.trim();
    if (!value || !values?.searchEnabled || !open) {
      searchReqIdRef.current += 1;
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      const reqId = ++searchReqIdRef.current;
      if (values?.searchType === "user") {
        dispatch(
          getClinicUsers(
            `?search=${value}&itemSize=30&page=1&userData=${petDataSharing}`,
          ),
        ).then((res) => {
          if (reqId !== searchReqIdRef.current) return;
          setSaleSearchUsers(res?.payload ?? []);
        });
      } else {
        dispatch(
          getClinicPetList({
            type: "all",
            page: 1,
            itemSize: 1000,
            petName: value,
            petData: petDataSharing,
          }),
        ).then((res) => {
          if (reqId !== searchReqIdRef.current) return;
          setSaleSearchPets(res?.payload?.data ?? []);
        });
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    values?.searchValue,
    values?.searchType,
    values?.searchEnabled,
    petDataSharing,
    dispatch,
    open,
  ]);

  const userListByFilter = () => {
    if (!values?.searchValue) return [];
    const search = values.searchValue.toLowerCase();
    return (saleSearchUsers ?? []).filter(
      (usr) =>
        (usr?.name ?? "").toLowerCase().includes(search) ||
        String(usr?.mobile ?? "").includes(search) ||
        (usr?.email ?? "").toLowerCase().includes(search) ||
        String(usr?.secondaryMobile ?? "").includes(search),
    );
  };

  const onChange = (name, value) => {
    const reqObj = { ...values, [name]: value };
    if (name === "backDated" && value) {
      reqObj.billDate = new Date();
    }
    if (name === "searchType") {
      reqObj.searchValue = "";
      reqObj.pet = null;
      reqObj.user = null;
      reqObj.userPet = null;
      setSaleSearchUsers([]);
      setSaleSearchPets([]);
      setPetsByUserId([]);
    }
    if (name === "user" || name === "pet") {
      reqObj.error = false;
      reqObj.searchEnabled = false;
      if (name === "user") {
        if (value?.id) {
          dispatch(getPetByUserId(value?.id)).then((res) => {
            if (res?.payload) {
              const reqList = (res?.payload ?? [])?.map((pt) => ({
                ...pt,
                name: pt?.petName,
                value: pt?.petId,
                userName: value?.name,
              }));
              setPetsByUserId(reqList);
            }
          });
        }
        reqObj.pet = null;
        reqObj.userPet = null;
      }
      if (name === "pet") reqObj.user = null;
    }
    if (name === "userPet") {
      reqObj.petSearch = "";
      reqObj.userPetErr = false;
    }
    if (name === "searchValue") {
      setSaleSearchUsers([]);
      setSaleSearchPets([]);
      reqObj.searchEnabled = true;
    }
    setValues(reqObj);
  };

  const handleClose = () => {
    setValues(initSaleValues);
    setSaleSearchUsers([]);
    setSaleSearchPets([]);
    setPetsByUserId([]);
    onClose?.();
  };

  const onCreate = () => {
    const userId =
      (values?.searchType === "user"
        ? values?.user?.id
        : values?.pet?.userId) ?? null;
    const petId =
      (values?.searchType === "user"
        ? values?.userPet?.petId
        : values?.pet?.petId) ?? null;

    const reqData = isEstimate
      ? {
          userId: userId || null,
          type: "Clinic",
          paymentOption: "",
          direct: [],
          discountValue: 0,
          discountType: "percentage",
          petId: petId || null,
          referenceNo: "",
          estimate: true,
        }
      : {
          type: values?.billAs,
          paymentOption: "draft",
          direct: [],
          discountValue: 0,
          discountType: "percentage",
          referenceNo: "",
          estimate: false,
          checkOut: "N",
          userId: userId || null,
          petId: petId || null,
        };

    if (!isEstimate && values?.backDated) {
      reqData.backDated = moment(new Date(values?.billDate)).format(
        "YYYY-MM-DD",
      );
    }

    dispatch(addDirectBilling(reqData)).then((res) => {
      if (res?.payload) {
        const paymentId = res?.payload?.data;
        handleClose();
        onCreated?.({
          paymentId,
          type: isEstimate ? "Clinic" : values?.billAs,
          estimate: isEstimate,
        });
      }
    });
  };

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      header={isEstimate ? "Create Estimate" : "Create Bill"}
      rightModal
      modalWidth={30}
    >
      <div className="scroll-85vh ph20">
        {!isEstimate ? ( //&& !isBillCombined
          <>
            <div className="font-medium fs14 black2">Bill as</div>
            <CustomSelect
              list={billAsForBillingHistory}
              value={values?.billAs}
              handleChange={(e) => onChange("billAs", e?.target?.value)}
              select
            />
          </>
        ) : null}

        <div className="font-medium fs14 black2 mt10">Search Type</div>
        <CustomSelect
          list={[
            { name: "Customer", value: "user" },
            { name: "Pet", value: "pet" },
          ]}
          value={values?.searchType}
          handleChange={(e) => onChange("searchType", e?.target?.value)}
          select
        />
        {values?.searchType === "user" ? (
          <div className="flex-end mt5">
            <Typography
              className="blue-color cursor txt-semi-bold fs14 underline"
              onClick={() => onAddCustomer?.(values)}
            >
              Add new customer
            </Typography>
          </div>
        ) : null}
        <div className={values?.searchType === "user" ? "mt5" : "mt15"} />
        <CustomTextField
          name="searchValue"
          fullWidth
          handleChange={(e) => onChange("searchValue", e?.target?.value)}
          value={values?.searchValue}
          placeholder={`Search ${
            values?.searchType === "user"
              ? "Customer by Name/Mobile/Email"
              : "Pet here..."
          }`}
          error={values?.error}
          helperText={
            values?.error
              ? `Please Select ${
                  values?.searchType === "user"
                    ? "Customer by Name/Mobile/Email"
                    : "Pet here..."
                }`
              : ""
          }
        />

        {values?.searchValue?.length > 0 &&
          values?.searchEnabled &&
          values?.searchType === "user" && (
            <Grid item xs={12}>
              {userListByFilter()?.length > 0 &&
                userListByFilter()?.map((usr) => (
                  <div
                    className="flex-row-ali-cen cursor mv10 cus-box"
                    key={usr?.id}
                    onClick={() => onChange("user", usr)}
                  >
                    <div className="w15Per">
                      <div className="flex-center">
                        <img
                          src={require("../../../assets/images/png/contact.png")}
                          className="imghw40"
                          alt=""
                        />
                      </div>
                    </div>
                    <div className="w75Per">
                      <div className="flex-coloum ml10">
                        <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                          Parent Name:
                          <Typography className="txt-regular fs12 black15 ml5">
                            {usr?.name}
                          </Typography>
                        </Typography>
                        <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                          Phone:
                          <Typography className="txt-regular fs12 black15 ml5">
                            {usr?.mobile}
                          </Typography>
                        </Typography>
                        {usr?.secondaryMobile?.length > 0 ? (
                          <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                            Secondary Phone:
                            <Typography className="txt-regular fs12 black15 ml5">
                              {usr?.secondaryMobile}
                            </Typography>
                          </Typography>
                        ) : null}
                        <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                          Email:
                          <Typography className="txt-regular fs12 black15 ml5">
                            {usr?.email}
                          </Typography>
                        </Typography>
                      </div>
                    </div>
                  </div>
                ))}
            </Grid>
          )}

        {values?.searchValue?.length > 0 &&
          values?.searchEnabled &&
          values?.searchType === "pet" && (
            <Grid item xs={12}>
              {(saleSearchPets ?? [])?.length > 0 &&
                (saleSearchPets ?? [])?.map((petDat) => {
                  const pet = petDat?.listOfDatas;
                  return (
                    <div
                      className="flex-row-ali-cen cursor mv10 cus-box"
                      key={pet?.petId}
                      onClick={() => onChange("pet", pet)}
                    >
                      <div className="w15Per">
                        <div className="flex-center">
                          <img
                            src={require("../../../assets/images/png/contact.png")}
                            className="imghw40"
                            alt=""
                          />
                        </div>
                      </div>
                      <div className="w75Per">
                        <div className="flex-coloum ml10">
                          <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                            Pet Name:
                            <Typography className="txt-regular fs12 black15 ml5">
                              {pet?.petName}
                            </Typography>
                          </Typography>
                          <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                            Parent Name:
                            <Typography className="txt-regular fs12 black15 ml5">
                              {pet?.userName}
                            </Typography>
                          </Typography>
                          <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                            Breed:
                            <Typography className="txt-regular fs12 black15 ml5">
                              {pet?.breed}
                            </Typography>
                          </Typography>
                          <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                            DOB:
                            <Typography className="txt-regular fs12 black15 ml5">
                              {pet?.dob
                                ? moment(new Date(pet?.dob)).format(
                                    "DD MMM YYYY",
                                  )
                                : "—"}
                            </Typography>
                          </Typography>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </Grid>
          )}

        {!values?.searchEnabled && (values?.pet?.petId || values?.user?.id) ? (
          <div className="flex-row-ali-cen cursor mv10 cus-box-selected light-blue-back6">
            <div className="w15Per">
              <div className="flex-center">
                <img
                  src={require("../../../assets/images/png/contact.png")}
                  className="imghw40"
                  alt=""
                />
              </div>
            </div>
            <div className="w75Per">
              <div className="flex-coloum ml10">
                {values?.pet?.petId ? (
                  <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                    Pet Name:
                    <Typography className="txt-regular fs12 black15 ml5">
                      {values?.pet?.petName}
                    </Typography>
                  </Typography>
                ) : null}
                <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                  Parent Name:
                  <Typography className="txt-regular fs12 black15 ml5">
                    {values?.user?.name ?? values?.pet?.userName}
                  </Typography>
                </Typography>
                {values?.user?.id ? (
                  <>
                    <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                      Phone:
                      <Typography className="txt-regular fs12 black15 ml5">
                        {values?.user?.mobile}
                      </Typography>
                    </Typography>
                    {values?.user?.secondaryMobile?.length > 0 ? (
                      <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                        Secondary Phone:
                        <Typography className="txt-regular fs12 black15 ml5">
                          {values?.user?.secondaryMobile}
                        </Typography>
                      </Typography>
                    ) : null}
                    <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                      Email:
                      <Typography className="txt-regular fs12 black15 ml5">
                        {values?.user?.email}
                      </Typography>
                    </Typography>
                  </>
                ) : null}
                {values?.pet?.petId ? (
                  <>
                    <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                      Breed:
                      <Typography className="txt-regular fs12 black15 ml5">
                        {values?.pet?.breed}
                      </Typography>
                    </Typography>
                    <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                      DOB:
                      <Typography className="txt-regular fs12 black15 ml5">
                        {values?.pet?.dob
                          ? moment(new Date(values?.pet?.dob)).format(
                              "DD MMM YYYY",
                            )
                          : "—"}
                      </Typography>
                    </Typography>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {values?.searchType === "user" && values?.user?.id ? (
          <>
            <div className="mt15" />
            <CustomTextField
              name="petSearch"
              fullWidth
              handleChange={(e) => onChange("petSearch", e?.target?.value)}
              value={values?.petSearch}
              placeholder="Search Pet here..."
              error={values?.userPetErr}
              helperText={values?.userPetErr ? "Please Select Pet" : ""}
            />
            {values?.userPet?.petId && !(values?.petSearch?.length > 0) ? (
              <div className="flex-row-ali-cen cursor mv10 cus-box-selected light-blue-back6">
                <div className="w15Per">
                  <div className="flex-center">
                    <img
                      src={require("../../../assets/images/png/contact.png")}
                      className="imghw40"
                      alt=""
                    />
                  </div>
                </div>
                <div className="w75Per">
                  <div className="flex-coloum ml10">
                    <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                      Pet Name:
                      <Typography className="txt-regular fs12 black15 ml5">
                        {values?.userPet?.petName}
                      </Typography>
                    </Typography>
                    <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                      Parent Name:
                      <Typography className="txt-regular fs12 black15 ml5">
                        {values?.user?.name ?? values?.userPet?.userName}
                      </Typography>
                    </Typography>
                  </div>
                </div>
              </div>
            ) : null}
            {values?.petSearch?.length > 0 ? (
              <Grid item xs={12}>
                {(petsByUserId ?? [])
                  ?.filter((pl) =>
                    (pl?.petName ?? "")
                      ?.toLowerCase()
                      ?.includes(values?.petSearch?.toLowerCase()),
                  )
                  ?.map((pet) => (
                    <div
                      className="flex-row-ali-cen cursor mv10 cus-box"
                      key={pet?.petId}
                      onClick={() => onChange("userPet", pet)}
                    >
                      <div className="w15Per">
                        <div className="flex-center">
                          <img
                            src={require("../../../assets/images/png/contact.png")}
                            className="imghw40"
                            alt=""
                          />
                        </div>
                      </div>
                      <div className="w75Per">
                        <div className="flex-coloum ml10">
                          <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                            Pet Name:
                            <Typography className="txt-regular fs12 black15 ml5">
                              {pet?.petName}
                            </Typography>
                          </Typography>
                          <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                            Parent Name:
                            <Typography className="txt-regular fs12 black15 ml5">
                              {pet?.userName}
                            </Typography>
                          </Typography>
                        </div>
                      </div>
                    </div>
                  ))}
              </Grid>
            ) : null}
          </>
        ) : null}

        {!isEstimate ? (
          <>
            <CustomCheckbox
              label="Back Dated"
              onChange={() => onChange("backDated", !values?.backDated)}
              checked={values?.backDated}
            />
            {values?.backDated ? (
              <CustomTextField
                fullWidth
                mobileDate
                value={values?.billDate}
                label="Bill Date"
                handleChange={(e) => onChange("billDate", e?.target?.value)}
                maxDate={new Date()}
              />
            ) : null}
          </>
        ) : null}

        <div className="flex1-end mt20">
          <div>
            <CustomButton smallBtn text="Create" onClick={onCreate} />
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default CreateBillEstimateModal;
